import { CdkAccordion } from '@angular/cdk/accordion';
import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ValidationErrors, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, filter, finalize, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { InteractionAnswer } from '../../../../models/interactions.model';
import { MediaContext } from '../../../../models/media-context.model';
import { MediaItem, MediaResponse } from '../../../../models/media.model';
import { MediaService } from '../../../../services/media.service';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { AnswerTreeNode } from '../../../../views/interaction-editor/interaction-editor.component';
import { ConfirmHandler } from '../../../dialog/dialog.model';
import { DialogService } from '../../../dialog/dialog.service';
import { MediaPickerDialogService } from '../../../media-picker-dialog/services/media-picker-dialog.service';
import { InteractionEditorStateService } from '../../services/interaction-editor-state.service';
import { AnswerListItemComponent } from '../answer-list-item/answer-list-item.component';

@Component({
  selector: 'app-answer-list',
  imports: [CdkAccordion, CdkDropList, MatIcon, MatIconButton, AnswerListItemComponent],
  templateUrl: './answer-list.component.html',
  styleUrl: './answer-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnswerListComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _mediaPickerDialog = inject(MediaPickerDialogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _mediaService = inject(MediaService);

  private readonly _interactionEditorState = inject(InteractionEditorStateService);
  private readonly _isImportingMedia = signal(false);

  protected readonly selectedQuestionId =
    this._interactionEditorState.selectedQuestionId;
  protected readonly selectedAnswerId = this._interactionEditorState.selectedAnswerId;

  protected readonly totalAnswers = computed(() => this.nodes().length);
  protected readonly maxAnswers = 6;

  protected readonly canAddAnswers = computed(
    () => this.selectedQuestionId() && this.totalAnswers() < this.maxAnswers
  );

  protected readonly canEditSelectedAnswer = computed(() => {
    const selectedId = this.selectedAnswerId();
    if (!selectedId) return true;

    const answer = this.nodes().find((a) => a._id === selectedId);
    if (!answer || answer.type !== 'image') return true;

    return this._permissionService.hasPermission('media:read');
  });

  public readonly nodes = input<AnswerTreeNode[]>([]);
  public readonly clientId = input<string | null>(null);

  public readonly disableControls = input(false, { transform: booleanAttribute });

  handleAnswerAdd() {
    if (this.disableControls()) {
      console.warn('Unable to add answer. Editor is locked');
      return;
    }

    if (!this.selectedQuestionId()) {
      console.warn('Unable to add answer. No question selected');
      return;
    }

    const existingAnswers = this.nodes();

    if (existingAnswers.length > 0) {
      const answerType = existingAnswers[0].type || 'text';

      if (answerType === 'image') {
        this._handleImageAnswerAdd();
      } else {
        this._handleTextAnswerAdd();
      }
    } else {
      this._handleAnswerTypeSelection();
    }
  }

  private _handleAnswerTypeSelection() {
    const allOptions = [
      { value: 'text', label: 'Tekst', checked: true },
      {
        value: 'image',
        label: 'Afbeelding',
        checked: false,
        requiredPermission: 'media:read',
      },
    ];

    const options = allOptions.filter(
      (option) =>
        !option.requiredPermission ||
        this._permissionService.hasPermission(option.requiredPermission)
    );

    if (options.length === 1) {
      this._handleTextAnswerAdd();
      return;
    }

    this._dialog.select({
      title: 'Kies antwoord type',
      message: 'Welk type antwoord wil je toevoegen?',
      options,
      confirmText: 'Volgende',
      cancelText: 'Annuleren',
      onConfirm: (result) =>
        of(result).pipe(
          filter((result) => result != null),
          tap((type) => {
            if (type === 'image') {
              this._handleImageAnswerAdd();
            } else {
              this._handleTextAnswerAdd();
            }
          })
        ),
    });
  }

  private _handleTextAnswerAdd() {
    this._handlePromptDialog((result) =>
      of(result).pipe(
        filter((result) => result != null),
        tap((value) => {
          this._interactionEditorState.emitEvent({
            entity: 'answers',
            action: 'create',
            type: 'text',
            value,
          });
        }),
        switchMap(() => this._interactionEditorState.status$),
        filter((state) => state === 'idle'),
        take(1)
      )
    );
  }

  private _handleImageAnswerAdd() {
    const clientId = this.clientId();
    const context = this._mediaContext();

    this._mediaPickerDialog.open(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          switchMap((media) => this._importIfNeeded(media, clientId, context)),
          tap((media) => {
            this._interactionEditorState.emitEvent({
              entity: 'answers',
              action: 'create',
              type: 'image',
              value: { mediaId: media.id },
            });
          }),
          switchMap(() => this._interactionEditorState.status$),
          filter((state) => state === 'idle'),
          take(1)
        ),
      {
        type: 'image',
        multi: false,
        context,
      }
    );
  }

  handleAnswerEdit() {
    if (this.disableControls()) {
      console.warn('Unable to edit answer. Editor is locked');
      return;
    }

    const selectedAnswerId = this.selectedAnswerId();

    if (!selectedAnswerId) {
      console.warn('No answer selected.');
      return;
    }

    const answer = this.nodes().find((answer) => answer._id === selectedAnswerId);

    if (!answer) {
      console.warn('No answer found with given id.');
      return;
    }

    const answerType = answer.type || 'text';

    if (answerType === 'image') {
      this._handleImageAnswerEdit(answer);
    } else {
      this._handleTextAnswerEdit(answer);
    }
  }

  private _handleTextAnswerEdit(answer: AnswerTreeNode) {
    this._handlePromptDialog(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          tap((value) => {
            this._interactionEditorState.emitEvent({
              entity: 'answers',
              action: 'update',
              id: answer._id,
              type: 'text',
              value,
            });
          }),
          switchMap(() => this._interactionEditorState.status$),
          filter((state) => state === 'idle'),
          take(1)
        ),
      {
        title: 'Antwoord bewerken',
        value: typeof answer.value === 'string' ? answer.value : '',
      }
    );
  }

  private _handleImageAnswerEdit(answer: AnswerTreeNode) {
    const clientId = this.clientId();
    const context = this._mediaContext();

    this._mediaPickerDialog.open(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          switchMap((media) => this._importIfNeeded(media, clientId, context)),
          tap((media) => {
            this._interactionEditorState.emitEvent({
              entity: 'answers',
              action: 'update',
              id: answer._id,
              type: 'image',
              value: { mediaId: media.id },
            });
          }),
          switchMap(() => this._interactionEditorState.status$),
          filter((state) => state === 'idle'),
          take(1)
        ),
      {
        type: 'image',
        multi: false,
        context,
      }
    );
  }

  private _mediaContext(): MediaContext {
    const clientId = this.clientId();
    return clientId ? { scope: 'client', clientId } : { scope: 'unit' };
  }

  private _importIfNeeded(
    mediaItem: MediaItem,
    clientId: string | null,
    context: MediaContext
  ): Observable<MediaItem> {
    if (!clientId || mediaItem.scope === 'client') {
      return of(mediaItem);
    }

    this._isImportingMedia.set(true);

    return this._mediaService.import(mediaItem.id, context).pipe(
      map((importedMedia) => ({
        ...mediaItem,
        id: importedMedia._id,
        fileId: importedMedia.fileId,
        scope: 'client' as const,
      })),
      finalize(() => this._isImportingMedia.set(false)),
      catchError((error) => {
        this._snackBar.open('Media import failed', '', { duration: 3000 });
        return throwError(() => error);
      })
    );
  }

  handleAnswerDelete() {
    if (this.disableControls()) {
      console.warn('Unable to delete answer. Editor is locked');
      return;
    }

    const selectedAnswerId = this.selectedAnswerId();

    if (!selectedAnswerId) {
      console.warn('No answer selected.');
      return;
    }

    this._dialog.confirm({
      title: 'Antwoord verwijderen?',
      message:
        'Weet je zeker dat je dit antwoord wilt verwijderen? Ook de bijbehorende acties worden dan verwijderd',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () => {
        this._interactionEditorState.emitEvent({
          entity: 'answers',
          action: 'delete',
          id: selectedAnswerId,
        });

        return this._interactionEditorState.status$.pipe(
          filter((state) => state === 'idle'),
          take(1)
        );
      },
    });
  }

  handleAnswerDropped(event: CdkDragDrop<AnswerTreeNode[], unknown, AnswerTreeNode>) {
    if (this.disableControls()) {
      console.warn('Unable to sort answer. Editor is locked');
      return;
    }

    const { currentIndex, previousIndex } = event;

    if (currentIndex === previousIndex) {
      return;
    }

    const previousState = this._interactionEditorState.interaction();

    if (!previousState) {
      console.warn('No interaction state found.');

      return;
    }

    const updatedAnswers = this.reorderActions(
      previousState.answers,
      event.item.data.questionId,
      previousIndex,
      currentIndex
    );

    this._interactionEditorState.setState({
      ...previousState,
      answers: updatedAnswers,
    });

    this._interactionEditorState.emitEvent({
      entity: 'answers',
      action: 'sort',
      id: event.item.data._id,
      index: currentIndex,
    });
  }

  reorderActions(
    actions: InteractionAnswer[],
    questionId: string,
    fromIndex: number,
    toIndex: number
  ) {
    const relatedAnswers = actions.filter((action) => action.questionId === questionId);
    const preservedAnswers = actions.filter(
      (action) => action.questionId !== questionId
    );

    const copiedAnswers = [...relatedAnswers];
    moveItemInArray(copiedAnswers, fromIndex, toIndex);

    const updatedActions = copiedAnswers.map((action, index) => ({
      ...action,
      index,
    }));

    return [...preservedAnswers, ...updatedActions];
  }

  private _handlePromptDialog(
    callback: ConfirmHandler<string>,
    options?: { title?: string; value?: string }
  ) {
    this._dialog.prompt(
      {
        title: options?.title ?? 'Antwoord toevoegen',
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        onConfirm: callback,
        input: {
          mode: 'input',
          label: 'Antwoord',
          placeholder: 'Vul een antwoord in',
          defaultValue: options?.value,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een antwoord in',
            },
            maxlength: {
              validator: Validators.maxLength(255),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;

                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }
}
